/**
 * A sticky thumbnail rail for the product media gallery grid presentation.
 * Highlights the thumbnail of the grid item currently in view and scrolls
 * the grid to an item when its thumbnail is clicked.
 *
 * @element media-grid-thumbnails
 */
class MediaGridThumbnails extends HTMLElement {
  #observer;
  #items = [];
  #buttons = [];

  connectedCallback() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return;

    this.#items = Array.from(gallery.querySelectorAll('.media-gallery__grid > li'));
    this.#buttons = Array.from(this.querySelectorAll('.media-gallery__thumbnail-rail-item'));

    if (this.#items.length === 0 || this.#buttons.length === 0) return;

    this.#buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.#items[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // A narrow horizontal band around the upper-middle of the scrollport decides
    // which grid item is "current". On desktop the media column scrolls itself,
    // so observe against that container rather than the viewport.
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.#scrollParent(gallery),
      rootMargin: '-35% 0px -55% 0px',
      threshold: 0,
    });
    this.#items.forEach((item) => this.#observer?.observe(item));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** Nearest scrollable ancestor, or null for the viewport. */
  #scrollParent(element) {
    for (let node = element?.parentElement; node; node = node.parentElement) {
      const { overflowY } = getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
    }
    return null;
  }

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const index = this.#items.indexOf(entry.target);
      if (index === -1) continue;

      this.#buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', buttonIndex === index ? 'true' : 'false');
      });

      // Keep the active tile centred in the strip. Set scrollTop directly rather
      // than calling scrollIntoView, which would also scroll every scrollable
      // ancestor — including the media column this rail sits inside.
      const activeButton = this.#buttons[index];
      if (activeButton && this.scrollHeight > this.clientHeight) {
        const target = activeButton.offsetTop - (this.clientHeight - activeButton.offsetHeight) / 2;
        this.scrollTop = Math.max(0, Math.min(target, this.scrollHeight - this.clientHeight));
      }
    }
  };
}

if (!customElements.get('media-grid-thumbnails')) {
  customElements.define('media-grid-thumbnails', MediaGridThumbnails);
}
